import { skyEquipment } from './77-sky-upgrades.mjs';
import {
  skyFlightFrame,
  skyFlightPointToLocal,
  skyFlightPointToWorld,
} from './92-sky-flight-frame.mjs';
/** Authored gold cockpit centres in world metres after each enemy's model scale.
 * The small target radii include the visual banking envelope for touch aiming. */
export const SKY_GUARDIAN_CRITICAL_RADIUS = 0.38;
const TARGETS = {
  scout: [0.2283, 0.23],
  flanker: [0.1833, 0.23],
  bulwark: [0.3895, 0.36],
  interceptor: [0.188, 0.23],
  weaver: [0.2406, 0.3],
  ace: [0.3089, 0.3],
};
/** Return the hittable cockpit centre/radius; unknown legacy enemies use scout. */
export function skyEnemyWeakpoint(enemy) {
  if (enemy.authoredEnemyRig && enemy.weakpoint) return enemy.weakpoint;
  const [offset, radius] = TARGETS[enemy.enemyClass] ?? TARGETS.scout;
  const frame = skyFlightFrame(enemy.contactFrame);
  if (frame) {
    const point = skyFlightPointToLocal(frame, enemy);
    if (point)
      return { ...skyFlightPointToWorld(frame, { ...point, y: point.y + offset }), radius };
  }
  return { x: enemy.x, y: enemy.y + offset, radius };
}
/** Resolve a bolt already intersecting a target. Armor cannot feed precision
 * rewards. Guardian cores only accept damage during their telegraphed opening.
 * precision=false resolves autonomous/bonus-target hits through body armor only.
 * A valid target.contactFrame measures radial quality in that aperture's local
 * XY plane; contact data stays in world metres. Invalid frames retain legacy
 * world-XY quality. No camera or presentation rotation affects damage. */
export function skyShotImpact(target, shot, precision = true) {
  if (target.kind === 'pillar')
    return Number.isFinite(target.hp) && target.obstacleForm
      ? { quality: 'body', multiplier: 1 }
      : { quality: 'armor', multiplier: 0 };
  if (target.authoredEnemyRig) {
    if (precision && shot.authoredCritical) return { quality: 'critical', multiplier: 2.5 };
    return ['bulwark', 'ace'].includes(target.enemyClass)
      ? { quality: 'armor', multiplier: 0.4 }
      : { quality: 'body', multiplier: 1 };
  }
  const boss = target.kind === 'boss';
  if (boss && !target.vulnerable) return { quality: 'armor', multiplier: 0 };
  if (target.authoredRig)
    return shot.authoredCritical === true && precision
      ? { quality: 'critical', multiplier: 1.75 }
      : { quality: 'body', multiplier: 0.55 };
  const point = boss
    ? (target.weakpoints?.find((value) => value.id === shot.weakpointId) ??
      target.weakpoint ??
      target)
    : skyEnemyWeakpoint(target);
  if (boss && target.anatomy && (shot.region === 'armor' || !point.exposed))
    return {
      quality: 'armor',
      multiplier: target.vulnerable && shot.region === 'armor' ? 0.12 : 0,
    };
  const radius = boss ? (point.criticalRadius ?? SKY_GUARDIAN_CRITICAL_RADIUS) : point.radius;
  const frame = skyFlightFrame(target.contactFrame);
  const localShot = frame && skyFlightPointToLocal(frame, shot);
  const localPoint = frame && skyFlightPointToLocal(frame, point);
  const radial =
    localShot && localPoint
      ? Math.hypot(localShot.x - localPoint.x, localShot.y - localPoint.y)
      : Math.hypot(shot.x - point.x, shot.y - point.y);
  if (precision && radial <= radius) return { quality: 'critical', multiplier: boss ? 1.75 : 2.5 };
  if (!boss && ['bulwark', 'ace'].includes(target.enemyClass))
    return { quality: 'armor', multiplier: 0.4 };
  return { quality: 'body', multiplier: 1 };
}
/** Six precision hits earn a bounded equipment-scaled double-cadence burst. Charge is suspended
 * during the boost and its two-second recovery, so autofire cannot self-renew. */
export function chargeSkyOverdrive(state) {
  if (state.overdriveTime > 0 || state.overdriveRecovery > 0) return;
  state.overdriveCharge++;
  if (state.overdriveCharge >= 6) {
    state.overdriveCharge = 0;
    state.overdriveDuration = skyEquipment(state.upgrades).overdriveSeconds;
    state.overdriveTime = state.overdriveDuration;
    state.overdriveRecovery = 2;
  }
}
/** Advance only active simulation time; menus and terminal states freeze timers. */
export function stepSkyOverdrive(state, dt) {
  if (state.overdriveTime > 0) state.overdriveTime = Math.max(0, state.overdriveTime - dt);
  else state.overdriveRecovery = Math.max(0, state.overdriveRecovery - dt);
}
